// Чат — единственный экран работы. SMY-114
//
// До 06.09.2026 один вопрос проходил через четыре экрана: уточнение (03),
// подтверждение (04), выполнение (05) и результат (06–08). Каждый переход
// стирал предыдущий, и хронология одного вопроса собиралась только в голове
// пользователя: что он спросил, что я уточнил, что делал и чем кончилось.
//
// Теперь это одна лента. Вопрос, уточняющие карточки, ход работы и карточка
// результата стоят друг под другом в том порядке, в каком происходили, и
// остаются на месте, когда разговор продолжается. Каждый новый вопрос заводит
// СВОЙ чат — он появляется слева, и вернуться к нему можно как к письму, а не
// как к «истории», где лежат обезличенные строки.
//
// Что осталось от прежних экранов: подтверждение (шит) — оно и было окном
// поверх, а не экраном; и заглушка «нет результата» — её больше нет вовсе,
// потому что показывать нечего только в пустом чате.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import {
  againLabel, asksWords, checkDeclared, clarifiedStep, clarifyLine, CLARIFICATION, formatUnit, FREE_FOLLOWUPS_SPENT, freeEditsLeft,
  humanSize,
  assumptionFieldUnit, assumptionPaidUnit, assumptionsPlace, nextSendCost, imagePromptNote, fieldLength, isCurrentResult, isTerminal, newAttemptKey, pluralize, retryCost, SCENARIO_COPY, START_UNSURE,
  withAssumptionEdit,
  UPLOAD_WARNING,
  type Assumption, type ClarifiedRequest, type ScenarioId, type UnitId, type UnitLabel,
} from '@smyslika/core'
import {
  afterEdit, ApiError, attempts, backInField, fromPause, holdsCards, leftField, OfflineError, outcomeCause,
  sentText, shownText, startReplay, unseenBubbles, withDeadline, withoutSent, withoutSentFile,
  type AttemptCause, type AttemptFile, type ChatArtifact, type ChatFeed, type Clarification, type EditResult,
  type FeedMessage, type Intent, type PendingAttempt, type RatingInput, type RunMeta, type StartRunInput,
} from '@smyslika/api-client'

import { useActiveRun } from '../active-run.tsx'
import { sessionToken, useSession } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { useScrollToEnd } from '../scroll.ts'
import { Shell } from '../shell/Shell.tsx'
import { isTaskAvailable, TASKS, taskBySlug, UNAVAILABLE_HINT } from '@smyslika/ui-tokens'
import { ResultCard } from '../cards/index.tsx'
import { ConfirmSheet } from './ConfirmSheet.tsx'
import { Onboarding } from './Onboarding.tsx'
import { RunCard } from './RunCard.tsx'
import { Answer, AnswerActions, Assumptions, Bubble, Card, Chip, Composer, Icon, Notice, RunLine, Sources } from '../ui/index.ts'

const MINUTE: UnitLabel = { one: 'минута', few: 'минуты', many: 'минут' }
const EDIT: UnitLabel = { one: 'правка', few: 'правки', many: 'правок' }

/**
 * Почему файл не подошёл — словами человека, а не кодом причины.
 *
 * Проверяется ДО загрузки: сказать «файл слишком большой» после отправки
 * двадцати мегабайт по мобильной сети поздно. Правила берутся из ядра, те же
 * самые, что проверит сервер, — иначе клиент пропустил бы то, что сервер
 * отвергнет, и наоборот.
 */
function fileProblem(file: File): string | null {
  const problem = checkDeclared(file.name, file.size, file.type)
  if (problem == null) return null
  if (problem.reason === 'too_large') {
    return `«${file.name}» больше ${humanSize(problem.maxBytes)} — столько за раз не принимаю.`
  }
  if (problem.reason === 'bad_filename') {
    return 'В имени файла есть невидимые служебные знаки — переименуй его и прикрепи снова.'
  }
  return 'Такой формат не подойдёт. Годятся PDF, Word, JPEG и PNG.'
}

/** Карточка одного уточнения: вопрос и готовые ответы. */
function Question({
  item,
  answer,
  onAnswer,
}: {
  item: Clarification
  answer: string | undefined
  onAnswer: (value: string) => void
}) {
  return (
    <Card className="clarify__card">
      <div className="clarify__question">{item.question}</div>
      {!asksWords(item) ? (
        <div className="clarify__options">
          {item.options.map((o) => (
            <Chip key={o} selected={answer === o} onClick={() => onAnswer(o)}>
              {o}
            </Chip>
          ))}
        </div>
      ) : (
        // Пустой список готовых ответов — так спрашивают про сумму или адрес.
        // Тогда отвечать надо словами, и поле внизу для этого и есть. Ответ,
        // набранный в поле, ждёт здесь, пока не ответят на остальные такие
        // карточки (`clarifiedStep`).
        answer != null && answer !== '' ? (
          <p className="clarify__free">Ответ: {answer}</p>
        ) : (
          <p className="clarify__free muted">Ответь словами в поле ниже.</p>
        )
      )}
    </Card>
  )
}

/**
 * Пустой чат: приветствие НАД полем ввода.
 *
 * С 07.09.2026 главная собрана вокруг поля: приветствие сверху, поле по
 * центру экрана, карточки задач под ним (`Tasks`). Раньше порядок был
 * обратный — приветствие, карточки, а поле внизу, куда его прижимает
 * подвал, — и главное действие экрана оказывалось самым дальним от глаз.
 *
 * Поле при этом ОДНО И ТО ЖЕ. Второй `Composer` для главной означал бы две
 * копии поля ввода, которые разъедутся при первой правке формулировки, — то
 * же правило, по которому карточки результата не рисуют поле сами.
 */
function Welcome() {
  const { me } = useSession()
  // Имя есть не у всех: третий шаг регистрации пропускается, и
  // заводится оно в профиле. Без имени здоровается без имени — «Привет,
  // пользователь!» хуже простого «Привет!».
  const name = me?.name?.trim()

  return (
    <div className="welcome">
      <div className="home__greeting">
        <h1>{name != null && name !== '' ? `Привет, ${name}!` : 'Привет!'}</h1>
        <h1>С чем помочь?</h1>
      </div>
    </div>
  )
}

/**
 * Карточки задач — ПОД полем ввода, в подвале.
 *
 * Стоят там, где стоит поле, а не в ленте: между полем и карточками не
 * должно быть ни границы подвала, ни прокрутки. Порядок внутри подвала —
 * поле, строка про списание, карточки: строка объясняет поле и уходит вместе
 * с ним, карточки предлагают начать иначе.
 *
 * Подписи «Или просто напиши — я сам пойму задачу» под ними больше нет
 * (07.09.2026). Она объясняла поле, стоявшее в другом конце экрана; когда
 * поле оказалось над самими карточками, объяснять стало нечего — курсор в
 * нём и говорит это лучше строки.
 */
function Tasks({ onPick }: { onPick: (slug: string) => void }) {
  const { reference } = useSession()

  return (
    <div className="home__grid">
      {TASKS.map((t) => {
        // Доступность решает сервер полем `available` в справочнике.
        // Активная карточка выключенного сценария довела бы человека до
        // выполнения и там отказала — уже после объявления списания.
        const on = isTaskAvailable(reference, t.scenario)
        return (
          <Card
            key={t.slug}
            kind="task"
            as="button"
            className={`home__card${on ? '' : ' home__card--off'}`}
            disabled={!on}
            onClick={on ? () => onPick(t.slug) : undefined}
          >
            <Icon name={t.icon} size={19} />
            <span className="home__card-title">{SCENARIO_COPY[t.scenario].card}</span>
            {/* Серая карточка без причины ничем не лучше отказа: место
                подсказки занимает объяснение, а не исчезает. */}
            <span className="home__card-hint">{on ? SCENARIO_COPY[t.scenario].hint : UNAVAILABLE_HINT}</span>
          </Card>
        )
      })}
    </div>
  )
}

/**
 * Старт или правка остались без ответа (`outcomeCause` в клиенте API): дошёл
 * ли запрос до сервера, неизвестно (SMY-352, 05.10.2026). Своим классом, а не
 * `OfflineError`: окну подтверждения и вызывающим его надо отличать от обрыва
 * распознавания — там запуска не было наверняка. Строку над полем рисует сама
 * попытка (`unsure`), а не текст ошибки: повод её меняется с каждой досылкой.
 */
class StartUnsure extends Error {
  constructor() {
    super('исход старта неизвестен')
  }
}

/**
 * Сколько продлений гостя эта вкладка уже объяснила строкой (SMY-357). На
 * модуле, а не в экране: экран монтируется заново при каждом переходе, а
 * объяснить продление надо один раз — и тогда, когда оно случилось до
 * первого монтирования.
 */
let renewalsShown = 0

/**
 * Текст отказа для строки над полем. SMY-357
 *
 * 401 — не отказ, а смена сессии: аккаунт уйдёт на экран входа, а гостю
 * молча заведут нового гостя, и экран объяснит это своей строкой. Сырое
 * «Сессия недействительна» здесь было бы третьим текстом об одном и том же.
 */
function failText(e: unknown): string | null {
  if (e instanceof StartUnsure) return null
  if (e instanceof ApiError && e.status === 401) return null
  if (e instanceof OfflineError) return 'Нет связи. Проверь интернет.'
  return e instanceof ApiError ? e.message : (e as Error).message
}

export function Chat() {
  const {
    chats: chatsApi, artifacts, files, guestRenewals, intent: api, me, reference, runs: runsApi, serverNow,
  } = useSession()
  const { run, chatId: runChat, free: runFree, start, adopt, inputOf, lost, chargeOf } = useActiveRun()
  const navigate = useNavigate()
  const params = useParams()
  const [search] = useSearchParams()

  const chatId = params.id
  // Карточка задачи ведёт в новый чат с заранее выбранным сценарием: адрес
  // помнит выбор, и перезагрузка вкладки его не теряет.
  const chosen = params.slug != null ? taskBySlug(params.slug)?.scenario : undefined

  const [feed, setFeed] = useState<ChatFeed | null>(null)
  const [sent, setSent] = useState<string[]>([])
  const [intent, setIntent] = useState<Intent | null>(null)
  /**
   * Карточки уточнений на экране сейчас — объект, по которому попытка узнаёт
   * свои (`holdsCards`): пока на экране её карточки, её ответы держат они.
   * Ссылкой, а не состоянием: эффект места от неё не зависит. Смена чата
   * карточки снимает и сбрасывает её сама (эффект ленты ниже) — эффект места
   * читает её уже после.
   */
  const cardsNow = useRef<Intent | null>(null)
  useEffect(() => {
    cardsNow.current = intent != null && intent.questions.length > 0 ? intent : null
  })
  /**
   * Вопрос, к которому сервер задал уточнения, — рядом с их карточками, а не в
   * пузырях. Кнопка под карточками брала его из последнего пузыря, а пузыри
   * снимают и лента, и конец запуска: нажатие уводило одни ответы или вопрос
   * досланной попытки — платный запуск не с тем текстом (ворота ревью 9,
   * ux-states-checker и reviewer). По объекту карточек: другие карточки —
   * другой вопрос.
   */
  const questionOf = useRef(new WeakMap<Intent, string>())
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [text, setText] = useState(search.get('text') ?? '')
  /** Вернуть фокус в поле: «Спросить другое» исчезает вместе с карточками, и фокус ушёл бы на страницу. */
  const [focusField, setFocusField] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [file, setFile] = useState<{ id: string; name: string } | null>(null)
  const [uploading, setUploading] = useState(false)
  /** Окно подтверждения; `field` — что стояло в поле: вернётся туда при отказе. */
  const [confirming, setConfirming] = useState<{ intent: Intent; request: ClarifiedRequest; field: string; cards: Intent | null } | null>(null)
  /** Карточка, на правку которой сервер ответил «уточнения кончились» (`freeLeft` ниже). */
  const [spentFor, setSpentFor] = useState<string | null>(null)
  /**
   * Попытка с неизвестным исходом (`StartUnsure`). Экран сам повторяет её тем
   * же ключом (эффект повтора ниже, SMY-449), а пока повтор не ответил, поле и
   * повторы закрыты: человек, нажавший сам, завёл бы новую попытку с новым
   * ключом — и, дойди первая, второй запуск. Её текст стоит пузырём в ленте, а
   * не в закрытом поле (`shownText`).
   */
  const [unsure, setUnsure] = useState<PendingAttempt | null>(null)
  /** Повод неизвестного исхода: по нему строка над полем, и он меняется с каждой досылкой. */
  const [cause, setCause] = useState<AttemptCause>('server')
  /** Попытка сейчас, с последним поводом, — для ухода с места. */
  const unsureNow = useRef<PendingAttempt | null>(null)
  const user = me?.userId ?? null
  /**
   * Человек — через ссылку: смена человека (продление гостя) не должна
   * перезапускать эффект места и уводить попытку на паузу к прежнему, её ведёт
   * эффект продления ниже.
   */
  const who = useRef(user)
  useEffect(() => {
    who.current = user
  })
  const screen = useRef(Symbol('чат кабинета'))
  /** Показать попытку или снять её; склад помнит показанную — вкладка не закроется молча. */
  const hold = useCallback((p: PendingAttempt | null) => {
    unsureNow.current = p
    setUnsure(p)
    if (p != null) setCause(p.cause)
    if (who.current != null) attempts.show(screen.current, who.current, p)
  }, [])
  /**
   * Попытка ушла этим же сеансом. Сменился он — продление гостя, выход, вход в
   * другой аккаунт, соседняя вкладка, — попытка не его: не встаёт ни на экран,
   * ни на паузу и не досылается (ворота ревью 5, security).
   */
  const ours = useCallback((p: PendingAttempt) => p.session === sessionToken(), [])
  /**
   * Попытка, снятая сменой сеанса (`lost` досылки), — до эффекта продления
   * гостя. Токен стирается сразу на 401, а новый гость встаёт через два
   * запроса: шаг досылки между ними иначе унёс бы текст, и эффект нашёл бы
   * экран пустым (ворота ревью 6, ux-states-checker). Сеанс, сменившийся без
   * продления, — вход в другой аккаунт в соседней вкладке — её текста не
   * получает: ни в поле, ни досылкой.
   */
  const orphan = useRef<PendingAttempt | null>(null)

  // Секундный тик — только ради строки про окно правки: без него «ещё 4
  // минуты» стояли бы неподвижно, пока человек не перезагрузит страницу.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])

  /** Идёт ли запуск ЭТОГО чата. Соседний показывать здесь нельзя. */
  const mine = run != null && (chatId == null ? runChat == null : runChat === chatId)
  const running = mine && run.status === 'running'

  /**
   * Чем кончилось чтение ленты открытого чата. SMY-356, 05.10.2026
   *
   * До этого любой отказ глотался, и ссылка на чат, которого у человека нет, —
   * удалённый, чужой, гостя другой вкладки — открывала пустую ленту с живым
   * полем. «Чат не найден» человек узнавал, только отправив вопрос, и вопрос
   * оставался висеть в поле. То же с первым чтением без связи: пустота вместо
   * разговора, а отправка уходила в чат, которого человек не видел.
   *
   * `missing` — сервер ответил 404: чата нет или он чужой, сервер их не
   * различает намеренно. `failed` — первое чтение не удалось иначе; при
   * перечитывании отказ по-прежнему не трогает уже показанную ленту.
   */
  const [loaded, setLoaded] = useState<'loading' | 'ready' | 'missing' | { failed: string | null }>('ready')
  const { refresh: refreshChats } = useWorkspace()

  /**
   * Чат на экране сейчас. Ответ ленты, пришедший уже после перехода в другой
   * чат, сверяется с ним и отбрасывается: иначе лента прошлого чата, а то и
   * его «Этого чата здесь нет», вставала бы под адрес нового (05.10.2026).
   */
  const shown = useRef(chatId)
  useEffect(() => {
    shown.current = chatId
  }, [chatId])

  /**
   * Перечитать ленту; отдаёт её — продолжению правки нужен свежий остаток
   * уточнений. `strict` — отказ чтения бросается дальше: продолжению правки
   * надо назвать его строкой, а не промолчать (ворота ревью 5).
   */
  const load = useCallback(async (strict = false): Promise<ChatFeed | null> => {
    if (chatId == null) {
      setFeed(null)
      setLoaded('ready')
      return null
    }
    try {
      // Строгое чтение ждёт продолжение правки под «Читаю задачу» — со сроком
      // попытки, иначе повисшее держало бы строку бессрочно (ворота ревью 5).
      const got = strict
        ? await withDeadline((signal) => chatsApi.feed(chatId, signal))
        : await chatsApi.feed(chatId)
      if (shown.current !== chatId) return null
      setFeed(got)
      setLoaded('ready')
      return got
    } catch (e) {
      if (shown.current !== chatId) return null
      if (e instanceof ApiError && e.status === 404) {
        setFeed(null)
        setLoaded('missing')
        return null
      }
      // Прежняя лента ОСТАЁТСЯ на экране. Перечитываем мы её после каждого
      // запуска, и обнулять на любой сетевой заминке значило бы стирать весь
      // разговор в ответ на одну неудачную попытку его обновить. Пустой она
      // становится только при переходе в другой чат — там это честно. А вот
      // первое чтение показать нечем, и его отказ объясняется словами.
      const why = e instanceof OfflineError ? 'Нет связи. Проверь интернет.' : e instanceof ApiError ? e.message : null
      setLoaded((was) => (was === 'loading' ? { failed: why } : was))
      if (strict) throw e
      return null
    }
  }, [chatsApi, chatId])

  useEffect(() => {
    setSent([])
    setIntent(null)
    setAnswers({})
    cardsNow.current = null
    setFeed(null)
    setSpentFor(null)
    setLoaded('loading')
    // Попытку с неизвестным исходом переносит эффект места ниже.
    void load()
  }, [load])

  /**
   * Где экран сейчас — для попытки с неизвестным исходом. SMY-449, ворота ревью
   * 06.10.2026
   *
   * Попытка показывается, только пока экран показывает её чат; у нового чата —
   * пока экран стоит на том же месте нового запроса (`/` и `/task/…` — один и
   * тот же экран с одним `chatId`, поэтому место — путь). Уход ставит попытку
   * на паузу у её чата (`attempts.park`), а вернувшись, экран забирает её и
   * досылает тем же ключом (решение владельца 06.10.2026, «пауза»). Попытку
   * нового чата поставить некуда: она снимается, а список чатов
   * перечитывается — дошедший запрос встанет там своим чатом.
   *
   * Место — путь, а не `location.key`: клик по открытому чату даёт новый ключ
   * при том же пути, и попытка уходила на паузу и возвращалась тем же объектом
   * — досылка вставала навсегда (ворота 4, reviewer и ux-states-checker).
   * Размонтированный экран (уход в раздел) — тоже уход (`mounted`).
   */
  const location = useLocation()
  const path = useRef(location.pathname)
  const mounted = useRef(true)
  // Флаг ставится и в установке: в разработке React проигрывает эффект дважды
  // (снять и поставить), и без этого экран навсегда считал бы себя снятым.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const latest = useRef({ refreshChats })
  useEffect(() => {
    latest.current = { refreshChats }
  })

  /** Экран показывает чат попытки — или то же место нового запроса. */
  const isHere = useCallback(
    (chat: string | null, at: string): boolean =>
      mounted.current && (chat != null ? shown.current === chat : path.current === at),
    [],
  )

  /** Снять из поля своё отправленное — набранное после ухода остаётся. */
  const dropSent = useCallback((field: string, sentFile: AttemptFile | null) => {
    setText((now) => withoutSent(now, field))
    if (sentFile != null) setFile((was) => withoutSentFile(was, sentFile))
  }, [])

  /**
   * Вернуть в поле текст и файл попытки, которую сервер не запустил: отказ,
   * правка без запуска, продление гостя. Набранное в поле остаётся
   * (`backInField`); текст и файл — два состояния, и каждое берёт свою половину
   * одного правила. `cards` — карточки уточнений на экране: свои держат текст
   * кнопки под ними сами.
   */
  const restore = useCallback((p: PendingAttempt, cards: Intent | null = cardsNow.current) => {
    setText((now) => backInField({ text: now, file: null }, p, cards).text)
    setFile((now) => backInField({ text: '', file: now }, p, cards).file)
  }, [])

  /**
   * Снять карточки уточнений, если экран всё ещё на чате отправки. Удачный
   * старт и снятая попытка нового чата, пришедшие «не здесь», оставляли
   * карточки с активной кнопкой: на новом запросе карточки хода работы нет, и
   * второе нажатие завело бы второй запуск с новым ключом (ворота ревью 7,
   * ux-states-checker). На другом чате карточки уже его — их не трогаем.
   */
  const dropCards = useCallback((chat: string | null) => {
    if ((shown.current ?? null) !== chat) return
    cardsNow.current = null
    setIntent(null)
    setAnswers({})
  }, [])

  /**
   * Перечитать список чатов сейчас и ещё раз, когда вернётся сеть: снятая
   * попытка нового чата, дойди она, встаёт там своим чатом, а без связи первое
   * чтение не пройдёт (ворота ревью 5, ux-states-checker). Слушатель —
   * вкладки, а не экрана: экран к этому времени бывает снят.
   */
  const refreshChatsSoon = useCallback(() => {
    void latest.current.refreshChats()
    window.addEventListener('online', () => void latest.current.refreshChats(), { once: true })
  }, [])

  /**
   * Показать попытку, стоящую на паузе у этого чата, — если экран на нём и
   * своей не показывает. Попытку другого сеанса склад не отдаёт.
   */
  const takeHere = useCallback(() => {
    // Экран всё ещё на этом чате: продолжение правки зовёт её после ожидания,
    // когда человек бывает уже в другом (ворота ревью 6, reviewer).
    if (chatId == null || !isHere(chatId, path.current) || who.current == null || unsureNow.current != null) return
    const parked = attempts.take(who.current, chatId, sessionToken())
    if (parked != null) hold(fromPause(parked))
  }, [chatId, isHere, hold])

  /**
   * Попытка с неизвестным исходом, пришедшая с ответом первой отправки. Её
   * текст уходит из поля: дальше он живёт в попытке. Здесь, и экран своей не
   * показывает, — показать; иначе — на паузу у её чата: экран, у которого своя
   * уже стоит, чужую не затирает, а заберёт её следом (ворота ревью 5,
   * reviewer). Попытку нового чата поставить некуда — снять и перечитать
   * список чатов. Другого сеанса — снять молча: поле не трогается, им ведает
   * продление гостя. `true` — показана здесь.
   */
  const land = useCallback(
    (p: PendingAttempt, at: string): boolean => {
      if (!ours(p)) return false
      // Из поля — то, что из него ушло (`leftField`), а не то, что вернётся при
      // отказе (ворота ревью 9).
      const gone = leftField(p)
      dropSent(gone.text, gone.file)
      if (isHere(p.chatId, at) && unsureNow.current == null) {
        hold(p)
        return true
      }
      if (who.current == null || !attempts.park(who.current, p, sessionToken())) refreshChatsSoon()
      // Её карточки уходят с экрана вместе с ней: ответы на них — в попытке, а
      // кнопка под ними завела бы второй запуск, пока попытку досылает другой
      // экран того же чата или пока она снята (ворота ревью 7 и 8).
      if (holdsCards(p, cardsNow.current)) dropCards(p.chatId)
      return false
    },
    [ours, isHere, hold, dropSent, refreshChatsSoon, dropCards],
  )

  useEffect(() => {
    path.current = location.pathname
    takeHere()
    // Попытка встаёт на паузу у этого чата и тогда, когда экран уже на нём:
    // ответ первой отправки приходит снятому экрану, если человек ушёл в раздел
    // и вернулся, — новый экран узнаёт о ней здесь (ворота ревью 5, reviewer).
    const off = attempts.subscribe((owner, chat) => {
      if (owner === who.current && chat === chatId) takeHere()
    })
    return () => {
      off()
      const left = unsureNow.current
      if (left == null) return
      hold(null)
      // Попытку другого сеанса склад не примет — она не этого человека.
      if (who.current == null || !attempts.park(who.current, left, sessionToken())) refreshChatsSoon()
      // Её карточки уходят вместе с ней — как в `land`.
      if (holdsCards(left, cardsNow.current)) dropCards(left.chatId)
    }
  }, [chatId, location.pathname, hold, takeHere, refreshChatsSoon, dropCards])

  // Человек пришёл позже экрана — сеанс читается при старте: попытку его чата,
  // стоящую на паузе, забираем, как только он известен.
  useEffect(() => {
    if (user != null) takeHere()
  }, [user, takeHere])

  // Чат мог исчезнуть с другого устройства, а слева он ещё стоит.
  useEffect(() => {
    if (loaded === 'missing') void refreshChats()
  }, [loaded, refreshChats])

  /**
   * Гостю молча завели нового гостя (SMY-357): сервер отверг прежнего —
   * срок сессии, уборка брошенных гостей, подменённый токен. Чаты прежнего
   * гостя этому не открываются: из такого чата — на главную, и экран тот же,
   * так что набранный текст остаётся в поле. Файл, уточнения и окно
   * подтверждения — прежнего гостя и сбрасываются. Отказ, на котором это
   * выяснилось, словами не показывается (`failText`), вместо него — строка
   * о том, что случилось и что делать.
   *
   * Счёт показанных — на вкладку (`renewalsShown`), а не на экран: при
   * открытии и перезагрузке продление кончается раньше, чем экран
   * смонтирован, и свой счёт он начал бы с уже случившегося продления —
   * строка не показалась бы вовсе.
   */
  useEffect(() => {
    if (guestRenewals <= renewalsShown) return
    renewalsShown = guestRenewals
    // Попытка прежнего гостя новому не досылается: снимаем её, а текст — в поле,
    // как строка ниже и обещает, — и ту, что досылка уже сняла сменой сеанса
    // (`orphan`). Карточки уточнений снимаются здесь же, и вопрос с ответами
    // возвращается целиком (карточек нет — `fieldOf`). Склад попыток прежнего
    // гостя чистит смена человека (`active-run.tsx`).
    const pending = unsureNow.current ?? orphan.current
    orphan.current = null
    if (pending != null) {
      hold(null)
      restore(pending, null)
    }
    setSent([])
    setIntent(null)
    setAnswers({})
    setFile(null)
    setConfirming(null)
    setError('Сессия без почты закончилась — прежние чаты больше не открываются. Отправь задачу ещё раз.')
    if (chatId != null) void navigate('/', { replace: true })
  }, [guestRenewals, chatId, navigate, hold, restore])

  /**
   * Оценить результат. SMY-123
   *
   * Лента перечитывается после сохранения, а не правится на месте: сервер
   * решает и что сохранилось, и надо ли ещё спрашивать. Обновив состояние у
   * себя, экран завёл бы второе мнение об этом — и первым расхождением стал
   * бы вопрос, заданный второй раз тому, кто уже ответил.
   *
   * Ошибка не глотается: её ловит и показывает сам компонент оценки — молча
   * потерянное нажатие человек считает сохранённым.
   */
  const rate = useCallback(
    async (runId: string, input: RatingInput) => {
      await runsApi.rate(runId, input)
      await load()
    },
    [runsApi, load],
  )

  // Запуск закончился — лента дописалась на сервере, и её надо перечитать.
  // Своими руками сообщение туда не кладём: две двери в одну ленту разошлись
  // бы на первом же ответе. Список чатов слева перечитывает не экран, а
  // запуск (`active-run.tsx`, SMY-355): экран, с которого ушли, не перечитал бы.
  const finished = mine && isTerminal(run.status)
  const settled = useRef<string | null>(null)
  useEffect(() => {
    if (!finished || run == null || settled.current === run.id) return
    settled.current = run.id
    void (async () => {
      await load()
      setSent([])
    })()
  }, [finished, run, load])

  // Запуск этого чата исчез, не закончившись у нас на глазах: сервер его не
  // знает (`gone` — рестарт при выкладке, или снимок пуст после долгого
  // отсутствия). Разбор осиротевших уже записал в ленту объяснение сбоя, а
  // экран ждал бы конца, которого не будет, — лента перечитывается сразу
  // (SMY-352). Сверка по номеру запуска, а не по «запуска не стало»: тот же
  // пустой запуск на миг ставит и `start`, и перечитка стёрла бы только что
  // отправленный вопрос.
  const seenRun = useRef<string | null>(null)
  useEffect(() => {
    if (mine) seenRun.current = run.id
  }, [mine, run])
  useEffect(() => {
    if (lost == null || lost !== seenRun.current) return
    seenRun.current = null
    void (async () => {
      await load()
      setSent([])
    })()
  }, [lost, load])

  // Живая плашка-предупреждение встала на виду — лента не тянет к концу, пока
  // идёт тот же запуск, до следующей своей отправки или смены чата (правило —
  // шапка `scroll.ts`, то же в приложении). Удержание помнит НОМЕР запуска, а
  // не флаг: новый запуск снимает его сам, а сброс по смене запуска шёл бы
  // эффектом родителя после эффекта плашки и стирал удержание в том же
  // коммите (ворота T6, круг 3). Сброс — в `useLayoutEffect`: он раньше
  // эффектов плашки. Отправка — рост `sent`: конец запуска ленту обнуляет
  // (`setSent([])`), и это не отправка.
  const warnedRun = useRef<string | null>(null)
  const sentBefore = useRef(sent.length)
  useLayoutEffect(() => {
    warnedRun.current = null
  }, [chatId])
  useLayoutEffect(() => {
    if (sent.length > sentBefore.current) warnedRun.current = null
    sentBefore.current = sent.length
  }, [sent.length])
  const held = { get current() { return run != null && warnedRun.current === run.id } }

  const feedRef = useScrollToEnd<HTMLDivElement>([
    feed?.messages.length,
    sent.length,
    run?.steps.length,
    run?.text,
    run?.status,
    intent,
  ], held)

  const artifactOf = useMemo(() => {
    const map = new Map<string, ChatArtifact>()
    for (const a of feed?.artifacts ?? []) map.set(a.runId, a)
    return map
  }, [feed])

  /**
   * Дошёл ли результат запуска до ленты.
   *
   * Пока не дошёл, ответ показывается из потока — иначе человек смотрел бы на
   * законченный процесс и пустоту под ним. Как только лента перечитана, тот же
   * ответ приходит из неё, и живую копию надо убрать: без этой проверки один
   * ответ стоял бы в ленте дважды.
   */
  const inFeed = useMemo(() => {
    const ids = new Set<string>()
    for (const m of feed?.messages ?? []) if (m.runId != null) ids.add(m.runId)
    return ids
  }, [feed])

  /**
   * Дошёл ли до ленты сам ответ запуска — сообщение, а не плашка.
   *
   * Плашка исхода — сбоя, отказа, отмены — тоже несёт номер запуска (SMY-352),
   * и по `inFeed` начатый ответ пропадал бы вместе с карточкой: человек жмёт
   * «Отменить» посреди ответа, лента перечитывается — и текст, который он
   * читал, исчезает. До 05.10.2026 он оставался, и довод «отмена тратит запас»
   * в шапке `freeFollowUpsLeft` держится как раз на «видел ответ». Живая копия
   * уходит только вместе с готовым ответом.
   */
  const answered = useMemo(() => {
    const ids = new Set<string>()
    for (const m of feed?.messages ?? []) if (m.runId != null && m.kind !== 'notice') ids.add(m.runId)
    return ids
  }, [feed])

  /**
   * Под каким ответом стоит «Исходил из: … Изменить» — правилом ядра
   * (`assumptionsPlace`, Р1): ответ последней списанной задачи, пока за ней
   * нет новой. Сообщение — последний ответ этого запуска: плашки темы после
   * ответа — тоже его сообщения, и блок встал бы под плашкой. SMY-497
   */
  const assumptionAt = useMemo(() => assumptionsPlace(feed?.runs ?? [], feed?.messages ?? []), [feed])
  /**
   * Запуск, чьё допущение меняют набранным в поле (SMY-497): ставит пункт
   * блока, уходит с отправкой полем `assumptionOf` — только для счёта, на
   * бесплатность не влияет. Снимается удачной отправкой и опустевшим полем.
   */
  const assumptionOf = useRef<string | null>(null)
  /**
   * Тот же запуск состоянием, а не только ссылкой: строка цены под полем
   * перерисовывается по нему и называет единицу ответа под блоком, пока в
   * поле правка допущения (В4-Б, 27.10.2026) — ту же, что серая строка блока.
   * Ссылка остаётся для отправки: её читают обработчики, не дожидаясь
   * перерисовки. Ставит и снимает обе одна функция.
   */
  const [assumptionRun, setAssumptionRun] = useState<string | null>(null)
  const markAssumption = useCallback((runId: string | null) => {
    assumptionOf.current = runId
    setAssumptionRun(runId)
  }, [])
  /**
   * Блок скрыт (таблица состояний SMY-497; ворота T4/T5, ux-states-checker):
   * при карточках уточнений — набранное ушло бы ответом на них; пока запуск
   * этого чата не дошёл до ленты, идёт отправка или исход неизвестен — блок
   * стоял бы под прошлым ответом над новым, и заготовка ушла бы правкой не
   * того результата, а поле при неизвестном исходе закрыто.
   *
   * По `inFeed`, а не по `running` (повторные ворота, ux-states-checker): от
   * конца запуска до перечитанной ленты на экране старая лента, и блок
   * вспыхивал под прошлым ответом; не перечиталась — так там и стоял. Каждый
   * законченный запуск кладёт в ленту сообщение со своим номером — ответ или
   * плашку исхода (`noteOutcome`), так что блок возвращается с перечиткой.
   */
  const assumptionsHidden =
    (intent != null && intent.questions.length > 0) || (mine && !inFeed.has(run.id)) || busy || unsure != null
  useEffect(() => {
    if (text.trim() === '') markAssumption(null)
  }, [text, markAssumption])
  // Поле едет за человеком между чатами, ссылка — нет: правка допущения из
  // чата A не засчитывается отправке в чате B (ворота T4, reviewer).
  useEffect(() => {
    markAssumption(null)
  }, [chatId, markAssumption])
  const pickAssumption = useCallback(
    (runId: string, a: Assumption) => {
      markAssumption(runId)
      setText((now) => withAssumptionEdit(now, a))
      setFocusField((n) => n + 1)
    },
    [markAssumption],
  )

  /** Сколько заняло и списалось ли — по каждому запуску этого чата. */
  const metaOf = useMemo(() => {
    const map = new Map<string, RunMeta>()
    for (const r of feed?.runs ?? []) map.set(r.runId, r)
    return map
  }, [feed])

  /** Последний результат чата — к нему относится бесплатная правка. */
  const lastArtifact = useMemo(() => {
    const list = feed?.artifacts ?? []
    return [...list].sort((a, b) => (a.finishedAt ?? '').localeCompare(b.finishedAt ?? '')).at(-1) ?? null
  }, [feed])

  /**
   * Запас бесплатных уточнений и правок. Сервер ответил на правку «уточнения
   * кончились» (`reload` у `afterEdit`) — запас последнего оплаченного
   * результата кончился, а лента на экране старая (SMY-336). До свежей ленты
   * экран считает его нулём сам (`spentFor` — правленая карточка, пока её
   * запуск последний оплаченный): плашка над полем и строка цены под ним
   * говорят правду, а следующая отправка уходит новой задачей — не той же
   * правкой по кругу и не бесплатным уточнением, которое сервер списал бы без
   * подтверждения. Не прочиталась лента — так и остаётся, без строки отказа
   * (ворота ревью 5, ux-writer-critic).
   */
  const freeLeft =
    lastArtifact != null && spentFor === lastArtifact.id && isCurrentResult(feed?.runs ?? [], lastArtifact.runId)
      ? 0
      : (feed?.freeFollowUpsLeft ?? 0)

  /**
   * Повтор сбоя из ленты — после перезагрузки, возврата в чат и выкладки.
   * SMY-352
   *
   * Сервер пишет на месте несостоявшегося ответа плашку исхода, и под
   * последней из них, если это сбой, стоит повтор. Только под последней: у
   * сбоя, после которого разговор пошёл дальше, повторять поздно. Не у правки:
   * её вход — карточка и текст — к ленте не приложен, а сама правка карточке
   * возвращена. Вход — запомненный вкладкой, если запуск заводила она (с
   * файлом и выбранной задачей), иначе текст вопроса и сценарий запуска. У
   * вопроса с файлом без запомненного входа повторять нечем — файл к ленте не
   * приложен, — и вместо кнопки подсказка, как повторить самому.
   *
   * Без запомненного входа пар нет и у уточнённого запроса: текст вопроса — это
   * тело ленты с блоком «Уточнения», и уходит он одним `text` (SMY-487). Модель
   * ответа пары видит, поисковик и картинка — вместе с вопросами; тема риска
   * считается по всему тексту (`cacheKey` в `classify.ts`). Разбирать блок
   * обратно в пары здесь не стали: тело — текст человека, и набранное им
   * «Уточнения:» стало бы парами, которых никто не задавал (docs/assistant-core.md §3.2).
   */
  const feedRetry = useMemo((): { input: StartRunInput; scenario: ScenarioId; unit: UnitId } | 'file' | null => {
    const list = feed?.messages ?? []
    const last = list.at(-1)
    if (last == null || last.kind !== 'notice' || last.runId == null) return null
    const meta = metaOf.get(last.runId)
    if (meta?.outcome !== 'failed' || meta.edit === true) return null
    const stored = inputOf(last.runId)
    if (stored != null) return { input: stored, scenario: meta.scenario, unit: meta.unit }
    // Вопрос — ближайшая реплика человека выше: плашки того же запуска
    // («звони 103» идёт перед объяснением) пропускаются.
    let i = list.length - 2
    while (i >= 0 && list[i]!.kind === 'notice' && list[i]!.runId === last.runId) i--
    const question = list[i]
    if (question == null || question.role !== 'user' || question.kind !== 'text') return null
    if (question.attachment != null) return 'file'
    return {
      input: { text: question.body, scenario: meta.scenario, ...(chatId != null ? { chatId } : {}) },
      scenario: meta.scenario,
      unit: meta.unit,
    }
  }, [feed, metaOf, inputOf, chatId])

  /**
   * Были ли в этом чате бесплатные уточнения вообще.
   *
   * Ноль означает разное: в новом чате их не было и не полагалось, а в
   * оплаченном — кончились. Разницу видно по журналу: уточнения открывает
   * оплаченный результат.
   */
  const hadPaid = (feed?.runs ?? []).some((r) => r.charged)

  /**
   * Что сказать про правку последнего результата под полем ввода.
   *
   * Правило своё у каждого сценария: у изображения окно в минутах, у
   * документа — три правки без срока. Считается по ВРЕМЕНИ СЕРВЕРА: часам
   * устройства здесь верить нельзя, на этом решении держатся деньги.
   *
   * Строка живёт под полем, а не в карточке: правку человек будет набирать
   * здесь, и обещание должно стоять там, где им пользуются.
   *
   * Названы оба пути, а не один. До 08.09.2026 строка говорила «просто напиши,
   * что поправить» — и уводила от вопроса ровно там, где его задают: поле под
   * результатом в макетах так и называется, «Спроси про этот результат».
   * Спросить теперь и правда можно (SMY-54), а строка об этом молчала.
   */
  const editHint = useMemo(() => {
    if (lastArtifact == null) return null
    const policy = reference?.scenarios.find((s) => s.id === lastArtifact.scenario)?.freeEdits
    if (policy == null) return null
    // Бесплатно столько правок, сколько позволяют и карточка, и общий запас
    // чата: правки и уточнения делят его (SMY-336, п. 6.4 правил). По одной
    // карточке подсказка обещала бы правку, которую сервер назовёт новой
    // задачей.
    // Запас — у последнего оплаченного результата: карточка, выданная раньше
    // него, бесплатных правок не имеет (SMY-337, `isCurrentResult`).
    const pool = isCurrentResult(feed?.runs ?? [], lastArtifact.runId) ? freeLeft : 0
    const left = freeEditsLeft(lastArtifact, serverNow(), policy, lastArtifact.editUses, pool)
    if (left === 0) return null

    if (policy.minutes == null) {
      return `Ещё ${left} ${pluralize(left, EDIT)} этого результата — без списания лимита. Напиши, что поправить, — или просто спроси про него.`
    }
    // Вниз, а не вверх: округление вверх обещает минуту, которой нет. Ноль
    // подтягивается до единицы — «ещё 0 минут» читается как «уже нельзя».
    const ms = Date.parse(lastArtifact.editWindowExpiresAt as string) - serverNow().getTime()
    const minutes = Math.max(1, Math.floor(ms / 60_000))
    return `Правка бесплатно — ещё ${minutes} ${pluralize(minutes, MINUTE)}. Напиши, что поправить, — или просто спроси про него.`
    // Пересчитывается тиком ниже: `tick` меняется раз в секунду.
  }, [lastArtifact, reference, serverNow, tick, freeLeft, feed])

  /**
   * Чем спишется правка допущения; null — уйдёт бесплатно (В4-Б, правило ядра
   * `assumptionPaidUnit`): тогда под блоком серая строка цены. Тем же
   * условием строка под полем говорит «Спишется … с лимита».
   */
  const assumptionPaid =
    assumptionAt == null ? null : assumptionPaidUnit(editHint != null, freeLeft, metaOf.get(assumptionAt.runId)?.unit)
  /**
   * Строка цены под полем — правилами ядра, теми же, что у блока: что обещает
   * следующая отправка (`nextSendCost`; при карточках набранное — ответ на
   * них, а не правка) и чем спишется (`assumptionFieldUnit`: пока в поле
   * правка допущения — единица ответа под блоком, как в серой строке).
   */
  const fieldCost = nextSendCost(editHint != null && !(intent != null && intent.questions.length > 0), freeLeft)
  const costUnit = assumptionFieldUnit(
    assumptionRun != null ? metaOf.get(assumptionRun)?.unit : undefined,
    intent != null || chosen != null,
    nextUnit(reference, intent, chosen),
  )

  /**
   * Старт с ключом попытки (SMY-449). Обрыв связи и ответ, не пришедший за
   * срок, значат «исход неизвестен», а не «не ушло»: попытка хранит вход и
   * ключ, и экран досылает тот же старт сам — сервер на тот же ключ отдаёт тот
   * же запуск. Ключ новый на каждую попытку: повтор сбоя — это новая задача, и
   * прежний ключ отдал бы ему тот же сбойный запуск.
   *
   * `at` — место, где действие началось: ответ, пришедший, когда экран
   * показывает уже другое, чужое поле и адрес не трогает (ворота ревью 4).
   * `field` и `sentFile` — что стояло в поле: снимется при удаче, вернётся при
   * отказе.
   */
  const launch = useCallback(
    async (
      input: StartRunInput,
      fromField: boolean,
      at: string,
      field: string,
      sentFile: AttemptFile | null,
      cards: Intent | null,
      notice?: string,
    ): Promise<{ runId: string; chatId?: string; free: boolean } | null> => {
      const key = newAttemptKey()
      // Сеанс — до ожидания: ответ бывает в пути до срока попытки, и сверять
      // надо с тем сеансом, которым запрос ушёл (ворота ревью 5, security).
      const session = sessionToken()
      try {
        const started = await withDeadline((signal) => start(input, key, signal, notice))
        // Ответ пришёл уже другому сеансу: запуск — прежнего, и экран нового его
        // не показывает; текст остаётся в поле (ворота ревью 6, security).
        if (sessionToken() !== session) return null
        if (isHere(input.chatId ?? null, at)) return started
        // Ушли, пока шёл старт: запуск идёт, его чат провайдер поставил в
        // список. Из поля снимается только своё — поле едет за человеком, — и
        // карточки, под которыми ушёл вопрос (`dropCards`).
        dropSent(field, sentFile)
        if (holdsCards({ kind: 'start', fromField, cards }, cardsNow.current)) dropCards(input.chatId ?? null)
        return null
      } catch (e) {
        const cause = outcomeCause(e)
        if (cause == null) throw e
        const pending: PendingAttempt = {
          kind: 'start',
          chatId: input.chatId ?? null,
          session,
          input,
          key,
          field,
          file: sentFile,
          cards,
          fromField,
          cause,
          ...(notice != null ? { notice } : {}),
        }
        if (land(pending, at)) throw new StartUnsure()
        return null
      }
    },
    [start, isHere, dropSent, land, dropCards],
  )

  /**
   * Старт принят. Одно продолжение на первую попытку и на её досылку после
   * обрыва (SMY-449): разойдись они, досылка оставляла бы текст в поле или не
   * переводила в новый чат. Вопрос встаёт пузырём и у досылки, а лента у неё
   * перечитывается — запуск мог и кончиться, пока не было связи; пузырь
   * снимается, только когда лента пришла: иначе вопрос пропал бы с экрана до
   * конца запуска (ворота ревью 5).
   */
  const accepted = useCallback(
    (
      started: { chatId?: string },
      input: StartRunInput,
      fromField: boolean,
      replay: boolean,
      field: string,
      sentFile: AttemptFile | null,
      cards: object | null,
    ) => {
      setSent((s) => [...s, sentText(input)])
      if (fromField) markAssumption(null)
      if (replay && chatId != null) {
        void (async () => {
          const fresh = await load()
          if (fresh != null) setSent((was) => unseenBubbles(was, fresh))
        })()
      }
      if (fromField) {
        dropSent(field, sentFile)
        // Досылка снимает только свои карточки: экран, забравший попытку с
        // паузы, бывает с чужими — уточнениями к другому вопросу (ворота ревью
        // 8, reviewer).
        if (!replay || holdsCards({ kind: 'start', fromField, cards }, cardsNow.current)) {
          setIntent(null)
          setAnswers({})
        }
      }
      settled.current = null
      // Новый чат получил номер только сейчас: адрес меняем заменой, чтобы
      // «назад» не возвращало на пустой экран, которого больше нет.
      if (chatId == null && started.chatId != null) void navigate(`/chat/${started.chatId}`, { replace: true })
    },
    [load, chatId, navigate, dropSent, markAssumption],
  )

  /** Завести запуск и, если чат был новым, перейти в него. */
  const go = useCallback(
    /**
     * `intentId` — ключ решения классификатора, если распознавание было
     * (SMY-124). Его нет у бесплатного уточнения: там ручка уточнения не
     * вызывается вовсе, и связывать нечего. `field` — что из поля уходит:
     * набранное — и у отправки, и у кнопки под уточнениями (SMY-619); ответы
     * держат карточки. `at` — место, где
     * действие началось. `cards` — карточки уточнений, чьи ответы идут в
     * `request.clarifications` (`PendingAttempt`); нет их — `null`.
     */
    async (
      request: ClarifiedRequest,
      scenario: ScenarioId,
      intentId: string | undefined,
      field: string,
      at: string,
      cards: Intent | null,
      notice?: string,
    ): Promise<void> => {
      const input: StartRunInput = {
        text: request.text,
        ...(request.clarifications != null && request.clarifications.length > 0 ? { clarifications: request.clarifications } : {}),
        scenario,
        ...(chatId != null ? { chatId } : {}),
        ...(file != null ? { fileIds: [file.id] } : {}),
        ...(chosen != null ? { chosen } : {}),
        ...(intentId != null ? { intentId } : {}),
        ...(assumptionOf.current != null ? { assumptionOf: assumptionOf.current } : {}),
      }
      const sentFile = file != null ? { id: file.id, name: file.name } : null
      const started = await launch(input, true, at, field, sentFile, cards, notice)
      if (started == null) return
      accepted(started, input, true, false, field, sentFile, cards)
    },
    [launch, accepted, chatId, file, chosen],
  )

  /**
   * Отправка. Сначала экран: на нём карточки уточнений — набранное это ответ на
   * них (A1, `clarifiedStep` в ядре), раньше правки и бесплатного уточнения
   * (SMY-487). Иначе три пути, и выбирает между ними сервер, а не поле ввода.
   *
   * 1. Открыто окно бесплатной правки последнего результата — пробуем правку.
   *    Ручка сама решит, правка это, вопрос о результате или новая задача
   *    (SMY-54); в двух последних случаях мы просто идём дальше обычным
   *    путём, и вопрос там подхватит бесплатное уточнение.
   * 2. Есть бесплатные уточнения — идём сразу обычным ответом, без
   *    распознавания. Классификация стоит денег на каждом запросе, а сервер
   *    всё равно понизит уточнение до чата: спрашивать его незачем.
   * 3. Иначе — распознавание, и оно решает, спрашивать ли и что запускать.
   */
  async function send(): Promise<void> {
    const value = text.trim()
    if (value === '' || busy) return
    // Место — в начале действия: распознавание идёт секунды, и уход за это
    // время должен быть виден и ему (ворота ревью 4, reviewer и ux-states).
    const at = path.current

    setBusy(true)
    setError(null)
    try {
      // Карточки уточнений на экране — набранное это ответ на них (A1), и
      // раньше правки: окно правки бывает открыто и при карточках, и ответ
      // уходил правкой результата, тратя бесплатную правку (ворота ревью
      // SMY-487, ux-states-checker).
      const shown = cardsNow.current
      if (shown != null) {
        await clarifiedGo(shown, at, value, value)
        return
      }
      if (lastArtifact != null && editHint != null && chatId != null) {
        // Правка — тоже запуск, только бесплатный: обрыв здесь — тот же
        // неизвестный исход, что у старта (`launch`), и досылка тем же ключом
        // не тратит вторую правку из запаса (SMY-449).
        const key = newAttemptKey()
        const artifactId = lastArtifact.id
        const session = sessionToken()
        let res: EditResult
        try {
          const from = assumptionOf.current ?? undefined
          res = await withDeadline((signal) => artifacts.edit(artifactId, value, key, signal, from))
        } catch (e) {
          const cause = outcomeCause(e)
          if (cause == null) throw e
          if (land({ kind: 'edit', chatId, session, artifactId, text: value, key, cause }, at)) throw new StartUnsure()
          return
        }
        // Ответ правки — прежнему сеансу, как и у старта: не подхватываем.
        if (sessionToken() !== session) return
        // Одно решение с досылкой (`afterEdit`): разойдись они, причина
        // остановки, дописанная здесь, до досылки не доехала бы.
        const next = afterEdit(res)
        if (!isHere(chatId, at)) {
          // Ушли, пока шла правка: заведённую подхватываем в её чат и снимаем
          // из поля её текст. Не заведено ничего — текст не отправлен и
          // остаётся в поле (ворота ревью 5, reviewer и ux-states-checker).
          if (next.next === 'adopt') {
            adopt(next.runId, chatId)
            dropSent(value, null)
          }
          return
        }
        if (next.next === 'adopt') {
          markAssumption(null)
          adopt(next.runId, chatId)
          setSent((s) => [...s, value])
          dropSent(value, null)
          settled.current = null
          return
        }
        // Запас этого результата кончился, а экран об этом не знал: лента
        // перечитывается только по завершении запуска, и остаток на экране
        // старый. Идти дальше по нему значило бы отправить текст бесплатным
        // уточнением, которое сервер спишет без подтверждения (SMY-336).
        // Запас считаем нулём сами (`spentFor`), перечитываем ленту и оставляем
        // текст в поле: плашка над ним скажет, почему следующее сообщение
        // спишется, — и тогда, когда лента не прочиталась.
        if (next.next === 'reload') {
          setSpentFor(artifactId)
          void load()
          return
        }
      }

      await sendOn(value, at)
    } catch (e) {
      const why = failText(e)
      if (why != null) setError(why)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Отправка мимо правки: бесплатное уточнение или распознавание, а за ним —
   * вопросы, окно подтверждения или запуск. Её же продолжает досылка правки,
   * на которую сервер ответил «вопрос о результате» или «новая задача»
   * (решение владельца 06.10.2026) — со свежим остатком уточнений `free`.
   * `field` — что из поля уходит с этой отправкой: удача снимает только его.
   */
  async function sendOn(value: string, at: string, free = freeLeft, field = value): Promise<void> {
    // На экране карточки уточнений — набранное это ответ на них, и отправка
    // идёт как кнопкой под ними, без нового распознавания (решение владельца
    // 08.10.2026, SMY-487; на какой вопрос — `clarifiedStep`). Раньше
    // набранное уходило новым запросом с приклеенными ответами, а исходный
    // вопрос терялся.
    //
    // ДО бесплатного уточнения: карточки бывают и при `freeLeft > 0` — второй
    // вопрос написан, пока шёл первый запуск, а тот кончился оплатой. Ответ
    // уходил бесплатным чатом без вопроса и пар, удача снимала карточки, ввод
    // терялся (ворота ревью SMY-487, ux-states-checker).
    const shown = cardsNow.current
    if (shown != null) {
      await clarifiedGo(shown, at, value, field)
      return
    }
    if (free > 0) {
      await go({ text: value }, 'chat', undefined, field, at, null)
      return
    }

    // Со сроком попытки, как у старта: повисшее распознавание держало бы
    // «Читаю задачу» и закрытую отправку бессрочно (ворота ревью 5). Сеанс —
    // до ожидания, как у старта.
    const session = sessionToken()
    const got = await withDeadline((signal) =>
      api.detect(
        {
          text: value,
          ...(chosen != null ? { chosen } : {}),
          ...(file != null ? { fileIds: [file.id] } : {}),
        },
        signal,
      ),
    )
    // Распознавание ответило уже другому сеансу: вопрос — прежнего, и под
    // новым его не запускают и не уточняют; текст остаётся в поле (ворота
    // ревью 7, security).
    if (sessionToken() !== session) return
    // Ушли, пока шло распознавание: его итог — не этого места. Уточнения, окно
    // и пузырь встали бы на чужом экране (ворота ревью 4).
    if (!isHere(chatId ?? null, at)) return

    if (got.questions.length > 0) {
      // Уточнения показываем и ждём ответа: запуска пока нет, значит и
      // списываться нечему.
      questionOf.current.set(got, value)
      setIntent(got)
      // Новые карточки — с чистыми ответами: прежние, кнопками или из поля,
      // встали бы ответом на чужой вопрос того же номера (ворота ревью 3).
      setAnswers({})
      setSent((s) => [...s, value])
      setText((now) => withoutSent(now, field))
      return
    }
    if (got.confirm != null) {
      setConfirming({ intent: got, request: { text: value }, field, cards: null })
      return
    }
    await go({ text: value }, got.scenario, got.intentId, field, at, null, got.notice)
  }

  /**
   * Повторить сбой тем же входом — текст, сценарий, файл, чат (SMY-352).
   *
   * Мимо распознавания и окна подтверждения: человек уже подтвердил ровно эту
   * задачу, а повтор стоит как первая попытка — сбой запас не тратит. Вопрос
   * встаёт пузырём сразу, как у обычной отправки: сервер записал его в ленту
   * заново, а перечитываем мы её по концу запуска.
   */
  async function retry(input: StartRunInput): Promise<void> {
    if (busy) return
    const at = path.current
    setBusy(true)
    setError(null)
    try {
      const started = await launch(input, false, at, '', null, null)
      if (started != null) accepted(started, input, false, false, '', null, null)
    } catch (e) {
      const why = failText(e)
      if (why != null) setError(why)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Досылка попытки с неизвестным исходом — тем же ключом (SMY-449), циклом
   * общего модуля (`startReplay`): сразу, по возврату сети и по шагу, в полёте
   * одна, каждая со сроком и только своим сеансом. Ответы — как у первой
   * попытки: запуск — то же продолжение (`accepted`); правка — одним решением с
   * отправкой (`afterEdit`), и «вопрос» или «новая задача» идут дальше обычным
   * путём по свежей ленте; наш отказ — его строкой. Что сервер не запустил,
   * возвращается в поле (`restore`). Кончилась попытка — экран забирает
   * следующую, ждавшую у этого чата на паузе.
   *
   * Уход с места досылку останавливает (эффект места ставит попытку на паузу),
   * и ответ после остановки сюда не доходит. Службы — через ссылку: `start` и
   * `adopt` провайдера меняются на каждом событии потока.
   */
  const replayWith = useRef({ start, artifacts, accepted, adopt, load, sendOn, takeHere })
  useEffect(() => {
    replayWith.current = { start, artifacts, accepted, adopt, load, sendOn, takeHere }
  })
  useEffect(() => {
    if (unsure == null) return
    const p = unsure
    const at = path.current
    // Снятая с экрана попытка экран больше не трогает: снимают её раньше, чем
    // React остановит досылку (продление гостя, уход).
    const current = (): boolean => unsureNow.current?.key === p.key
    return startReplay<{ chatId?: string; free: boolean } | EditResult>({
      send: (signal) => {
        const { start, artifacts } = replayWith.current
        return p.kind === 'start' ? start(p.input, p.key, signal, p.notice) : artifacts.edit(p.artifactId, p.text, p.key, signal)
      },
      alive: () => ours(p),
      lost: () => {
        if (!current()) return
        hold(null)
        orphan.current = p
        replayWith.current.takeHere()
      },
      done: (result) => {
        if (!current()) return
        hold(null)
        const { accepted, adopt, load, sendOn, takeHere } = replayWith.current
        if (p.kind === 'start') {
          accepted(result as { chatId?: string; free: boolean }, p.input, p.fromField, true, p.field, p.file, p.cards)
          takeHere()
          return
        }
        const next = afterEdit(result as EditResult)
        if (next.next === 'adopt') {
          adopt(next.runId, p.chatId)
          setSent((s) => [...s, p.text])
          settled.current = null
          void (async () => {
            const fresh = await load()
            if (fresh != null) setSent((was) => unseenBubbles(was, fresh))
          })()
          takeHere()
          return
        }
        // Сервер ничего не запустил: текст правки — обратно в поле.
        restore(p)
        if (next.next === 'reload') {
          // Запас кончился, а остаток на экране старый: считаем его нулём сами,
          // как у первой отправки (`spentFor`), — плашка встаёт и без свежей
          // ленты (ворота ревью 5, ux-writer-critic).
          setSpentFor(p.artifactId)
          void load()
          takeHere()
          return
        }
        // Вопрос о результате или новая задача — дальше обычным путём (решение
        // владельца 06.10.2026), но по свежей ленте: досылка могла начаться до
        // её чтения, и остаток бесплатных уточнений был бы нулём (ворота 4).
        // Не прочиталась лента — отказ строкой, текст уже в поле (ворота 5).
        setBusy(true)
        void (async () => {
          try {
            const fresh = await load(true)
            // Сеанс сменился, пока читалась лента: продолжение — прежнего
            // человека, под новым его не отправляют (ворота ревью 7, security).
            if (fresh != null && ours(p)) await sendOn(p.text, at, fresh.freeFollowUpsLeft ?? 0, p.text)
          } catch (e) {
            const why = failText(e)
            if (why != null && isHere(p.chatId, at)) setError(why)
          } finally {
            setBusy(false)
            replayWith.current.takeHere()
          }
        })()
      },
      refused: (e) => {
        if (!current()) return
        hold(null)
        // Отказ — ничего не ушло: текст и файл попытки — обратно в поле.
        restore(p)
        setError(failText(e))
        replayWith.current.takeHere()
      },
      waiting: (next) => {
        if (!current()) return
        setCause(next)
        if (unsureNow.current != null) unsureNow.current = { ...unsureNow.current, cause: next }
      },
      onOnline: (listener) => {
        window.addEventListener('online', listener)
        return () => window.removeEventListener('online', listener)
      },
    })
  }, [unsure, hold, ours, restore, isHere])

  /**
   * Запуск уточнённого: вопрос карточек и ответы парами (`clarifiedStep` в
   * ядре — одно правило с приложением). `typed` — набранное в поле при
   * карточках, `field` — что из поля уходит. `launch` — кнопка под карточками:
   * она запускает всегда, а набранное уводит, как отправка. До e2e SMY-619
   * кнопка набранное не брала: задача уходила без него, а оно оставалось в
   * поле, и человек не знал, ушло ли.
   *
   * Ответ на карточку без кнопок, после которого такие карточки ещё ждут, не
   * запускает, а встаёт на неё (решение владельца 14.10.2026, `clarifiedStep`):
   * поле очищается для следующей.
   */
  async function clarifiedGo(cards: Intent, at: string, typed = '', field = '', launch = false): Promise<void> {
    const step = clarifiedStep({ question: questionOf.current.get(cards) ?? '', questions: cards.questions, answers, typed, launch })
    if (step.next === 'hold') {
      setAnswers((a) => ({ ...a, [step.index]: step.answer }))
      dropSent(field, null)
      return
    }
    const request = step.request
    if (cards.confirm != null) setConfirming({ intent: cards, request, field, cards })
    else await go(request, cards.scenario, cards.intentId, field, at, cards, cards.notice)
  }

  /**
   * «Спросить другое» под карточками (решение владельца 08.10.2026, SMY-487).
   * Пока карточки на экране, любое набранное — ответ на них, и без выхода новый
   * запрос ушёл бы платным ответом на старый вопрос; «Новый запрос» в меню
   * ведёт на тот же экран и карточек не снимает. Снимаются карточки, ответы и
   * пузырь вопроса, а с ними строка ошибки — запуска не было, списывать нечего.
   * Набранное остаётся в поле, а пустое получает вопрос назад: он не записан ни
   * в ленту, ни куда-то ещё, и промах по кнопке стоил бы его текста (ворота
   * ревью 2, ux-states-checker). Следующая отправка — обычная: правка,
   * бесплатное уточнение или распознавание.
   */
  function askOther(): void {
    if (intent == null) return
    const question = questionOf.current.get(intent)
    dropCards(shown.current ?? null)
    setError(null)
    if (question != null) {
      setSent((s) => {
        const at = s.lastIndexOf(question)
        return at < 0 ? s : [...s.slice(0, at), ...s.slice(at + 1)]
      })
      setText((now) => (now.trim() === '' ? question : now))
    }
    setFocusField((n) => n + 1)
  }

  /** Запустить то, что уже уточнили: кнопка под карточками вопросов. */
  async function runClarified(): Promise<void> {
    if (intent == null || tooLong) return
    const at = path.current
    const value = text.trim()
    setBusy(true)
    setError(null)
    try {
      await clarifiedGo(intent, at, value, value, true)
    } catch (e) {
      const why = failText(e)
      if (why != null) setError(why)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Загрузка приложенного файла.
   *
   * Проверка формы и размера идёт до отправки — правилами из ядра, теми же,
   * что проверит сервер. Сервер проверяет ещё и содержимое (расширение может
   * врать), и его отказ тоже показывается словами.
   */
  async function attach(picked: File): Promise<void> {
    const problem = fileProblem(picked)
    if (problem != null) {
      setError(problem)
      return
    }
    setError(null)
    setUploading(true)
    try {
      const { fileId } = await files.upload(picked)
      setFile({ id: fileId, name: picked.name })
      // Карточки снимаются целиком — с ответами: ответ из поля, вставший на
      // карточку, иначе пережил бы их и ушёл ответом на вопрос того же номера у
      // следующих карточек (ворота ревью 3, security).
      dropCards(shown.current ?? null)
    } catch (e) {
      const why =
        e instanceof OfflineError
          ? 'Нет связи — файл не ушёл. Проверь интернет и попробуй снова.'
          : e instanceof ApiError
            ? e.message
            : 'Файл не загрузился. Попробуй ещё раз.'
      // Строку неизвестного исхода это не перебивает: её рисует попытка, и
      // она стоит над остальными (`errorLine`).
      setError(why)
    } finally {
      setUploading(false)
    }
  }

  // Разбор документа без документа не запускается вовсе: предупреждение и
  // подсказка про скрепку нужны именно здесь, а не на всех экранах подряд.
  const needsFile = (intent?.scenario ?? chosen) === 'doc_review'
  /**
   * Длина запроса картинки. SMY-186
   *
   * В картинку текст уходит двумя путями: задачей «картинка» — выбранной
   * карточкой или распознанной — и правкой последней картинки, пока правка
   * бесплатна. Предупреждение, а не запрет: предел по замеру, а не аварийный,
   * и запрос длиннее уходит как есть (`IMAGE_PROMPT_MAX_CHARS` в ядре).
   */
  const drawsPicture =
    (intent?.scenario ?? chosen) === 'image' || (editHint != null && lastArtifact?.scenario === 'image')
  const imageNote = drawsPicture ? imagePromptNote(text.trim().length) : null
  /**
   * Длина запроса. SMY-619
   *
   * Сверх предела отправка закрыта той же проверкой, по которой сервер
   * отвечает 400, а поле правится — сократить написанное надо здесь же. Счёт —
   * по тому тексту, что уйдёт (`fieldLength` в ядре): при отвеченных карточках
   * набранное приклеивается к вопросу, сервер считает склейку, а строка
   * говорит о добавке. Кнопка под карточками уводит набранное так же, как
   * отправка, и закрыта вместе с ней (e2e SMY-619).
   */
  const cardsOnScreen = intent != null && intent.questions.length > 0 ? intent : null
  const { note: lengthNote, tooLong, closed: sendClosed } = fieldLength(
    cardsOnScreen != null
      ? { question: questionOf.current.get(cardsOnScreen) ?? '', questions: cardsOnScreen.questions, answers }
      : null,
    text,
  )
  const off = chosen != null && !isTaskAvailable(reference, chosen)
  // Приветствие и сетка задач — признак НОВОГО чата, а не пустой ленты.
  // По ленте это считалось раньше, и открытый чат, лента которого не
  // догрузилась, показывал «Привет! С чем помочь?» поверх своего разговора.
  // Попытка с неизвестным исходом — уже не пустой чат: её пузырь стоит в ленте.
  const empty = chatId == null && sent.length === 0 && !mine && unsure == null
  /**
   * Главная: пустой чат без выбранной задачи.
   *
   * Одно условие на три места — раскладку ленты, раскладку подвала и
   * карточки под полем. Три отдельных выражения разъехались бы: экран,
   * у которого поле уже по центру, а карточки ещё в ленте, собрать проще,
   * чем заметить.
   */
  const home = empty && chosen == null

  const liveArtifact = mine ? run.artifact : undefined
  const liveUnit: UnitId = (run?.unit as UnitId) ?? 'questions'
  const failed = finished && run.outcome !== 'done'
  const blocked = finished && run.outcome === 'blocked'
  /** С чем повторить сбой этого чата — если запуск заводила эта вкладка. */
  const again = mine && failed ? inputOf(run.id) : undefined
  /**
   * Сколько занял запуск, по часам клиента.
   *
   * Здесь это законно, а в списаниях — нет: время выполнения не деньги, и
   * ошибка на пару секунд ничего не решает. `finishedAt` снимка запуска нет
   * вовсе, а перечитанная лента приносит серверное время и подменяет это.
   *
   * ⚠️ Снимается ОДИН РАЗ, в момент завершения. Раньше это же выражение стояло
   * прямо в разметке и считалось на каждой перерисовке: строка «Выполнилось
   * за N с» над готовым ответом росла, пока экран открыт (найдено на проде
   * 06.09.2026). Ошибка тихая — число правдоподобное, просто чужое.
   */
  const startedAt = run?.startedAt
  /**
   * На какое число собраны источники у ЖИВОГО ответа — день самого запуска.
   *
   * Перечитанная лента приносит серверную дату (`meta.sourcesAt`) и подменяет
   * эту; до перечитывания брать неоткуда, кроме отметки старта. `Date.now()`
   * здесь был бы той же ошибкой, что и с длительностью: число правдоподобное,
   * но чужое — у запуска, начатого до полуночи, оно уедет на сутки вперёд.
   */
  const liveSourcesAt = startedAt != null ? startedAt.slice(0, 10) : undefined
  const [liveDuration, setLiveDuration] = useState<number | undefined>(undefined)
  useEffect(() => {
    if (!mine || !finished || startedAt == null) {
      // Новый запуск начинается с чистого листа: длительность прошлого,
      // оставшаяся на экране, подписала бы собой чужой ответ.
      if (!finished) setLiveDuration(undefined)
      return
    }
    setLiveDuration((was) => was ?? Math.max(0, Date.now() - Date.parse(startedAt)))
  }, [mine, finished, startedAt])

  // Чата нет или он не открылся: поля ввода нет — отправить вопрос некуда, а
  // отправка в такой чат кончилась бы тем же отказом, только позже (SMY-356).
  // Кроме не открывшегося чата, где уже идёт работа или ушёл вопрос: у только
  // что заведённого чата первое чтение ленты падает без связи, а запуск идёт,
  // и заглушка спрятала бы его карточку с «Отменить» — вопрос казался бы
  // пропавшим. Там экран остаётся, а причина встаёт строкой над полем.
  // Пока жива попытка, чат не «не открылся»: строка о ней важнее заглушки, а
  // досылка за заглушкой шла бы незаметно (ворота ревью 4, ux-states-checker).
  const unopened = typeof loaded === 'object' && !mine && sent.length === 0 && unsure == null
  // Строка неизвестного исхода — над остальными: она объясняет закрытое поле,
  // а повод в ней меняется с каждой досылкой (решение владельца 06.10.2026,
  // «своя строка»).
  const errorLine = unsure != null ? START_UNSURE.web[cause] : (error ?? (typeof loaded === 'object' ? loaded.failed : null))
  if (loaded === 'missing' || unopened) {
    return (
      <Shell>
        <div className="chat">
          {loaded === 'missing' ? (
            <ChatGone
              title="Этого чата здесь нет"
              text="Его удалили или он в другом аккаунте. Если аккаунт твой — войди в него. Без почты чат открывается только во вкладке, где его начали."
              action="Новый запрос"
              onAction={() => void navigate('/')}
            />
          ) : (
            <ChatGone
              title="Чат не открылся"
              text={loaded.failed}
              action="Открыть ещё раз"
              onAction={() => {
                setLoaded('loading')
                void load()
              }}
            />
          )}
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className={`chat${home ? ' chat--home' : ''}`} ref={feedRef}>
        {/* Лента открытого чата ещё не пришла. Только когда показать больше
            нечего: у только что заведённого чата в это время уже стоят вопрос
            и карточка хода работы. Без кнопки: ожидание — не тупик. */}
        {loaded === 'loading' && chatId != null && feed == null && !mine && sent.length === 0 ? (
          <p className="muted chat__loading" role="status">
            Открываю чат
          </p>
        ) : null}

        {/* Приветствие — только в ПУСТОМ чате без выбранной задачи. Человек,
            нажавший «Создать документ», уже выбрал: здороваться с ним второй
            раз и показывать тот же выбор значит спрашивать заново. */}
        {home ? <Welcome /> : null}

        {/* Сценарий выключен: сюда попадают адресом, а не с карточки. */}
        {off ? <Notice>{UNAVAILABLE_HINT}</Notice> : null}

        {empty && chosen != null && !off ? <Bubble from="assistant">{SCENARIO_COPY[chosen].ask}</Bubble> : null}

        {(feed?.messages ?? []).map((m) => {
          const found = m.runId != null ? artifactOf.get(m.runId) : undefined
          const meta = m.runId != null ? metaOf.get(m.runId) : undefined
          return (
            <FeedRow
              key={m.id}
              message={m}
              onRate={rate}
              {...(found != null ? { artifact: found } : {})}
              {...(meta != null ? { meta } : {})}
              {...(assumptionAt != null && assumptionAt.messageId === m.id && !assumptionsHidden
                ? {
                    assumptions: assumptionAt.list,
                    onAssumption: (a: Assumption) => pickAssumption(assumptionAt.runId, a),
                    assumptionsPaid: assumptionPaid,
                  }
                : {})}
            />
          )
        })}

        {/* Отправленное в этой сессии: сервер уже записал его в ленту, но
            перечитываем мы её только по завершении запуска. До того вопрос
            должен стоять на экране — иначе он выглядит непринятым. */}
        {sent.map((s, i) => (
          <Bubble key={`sent-${i}`} from="user">
            {s}
          </Bubble>
        ))}

        {/* Попытка с неизвестным исходом — пузырём, а не текстом в закрытом
            поле: поле остаётся человеку, а в кабинете оно одно на все чаты, и,
            вернувшись в чат, человек застал бы в нём набранное в другом (ворота
            ревью 5, ux-states-checker). Что с ней — говорит строка ниже. */}
        {unsure != null ? (
          <Bubble from="user">
            {unsure.kind === 'start' && unsure.file != null ? (
              <span className="bubble__file">
                <Icon name="docReview" size={16} strokeWidth={2} />
                {unsure.file.name}
              </span>
            ) : null}
            {shownText(unsure)}
          </Bubble>
        ) : null}

        {intent != null && intent.questions.length > 0 ? (
          <>
            <Bubble from="assistant">{clarifyLine(intent.scenario, intent.questions.length)}</Bubble>
            <div className="clarify__cards">
              {intent.questions.map((q, i) => (
                <Question
                  key={q.question}
                  item={q}
                  answer={answers[i]}
                  onAnswer={(v) => setAnswers((a) => ({ ...a, [i]: v }))}
                />
              ))}
              {/* Успокоение стоит под вопросами, а не под кнопкой: человек
                  читает его в тот момент, когда решает, отвечать ли. */}
              <p className="clarify__calm">
                <span className="clarify__dot" />
                Уточнения не списываются с лимита — считается только готовый результат
              </p>
              <div className="clarify__go">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={busy || unsure != null || tooLong}
                  onClick={() => void runClarified()}
                >
                  {intent.confirm?.action ?? SCENARIO_COPY[intent.scenario].action}
                </button>
                <button type="button" className="btn btn--ghost" disabled={busy || unsure != null} onClick={askOther}>
                  Спросить другое
                </button>
              </div>
            </div>
          </>
        ) : null}

        {/* Признак неотложного состояния — ДО хода работы и до ответа.
            Правило из docs/disclaimers.md: сначала телефон экстренной службы,
            потом всё остальное. Ждать конца ответа здесь нельзя — ответ идёт
            секунды, а решение человек принимает сейчас. */}
        {mine && !inFeed.has(run.id)
          ? (run.notices ?? [])
              .filter((n) => n.placement === 'before')
              .map((n) => (
                <Notice key={n.text} live onShown={() => (warnedRun.current = run.id)}>
                  {n.text}
                </Notice>
              ))
          : null}

        {/* Строка 03б — что спишется, когда шита не было (SMY-494). Ниже
            неотложной плашки: телефон экстренной службы — первым. Только пока
            запуск идёт: у законченного остаток в ней уже неверен. */}
        {mine && !inFeed.has(run.id) && !isTerminal(run.status) && chargeOf(run.id) != null ? (
          <Notice>{chargeOf(run.id)}</Notice>
        ) : null}

        {/* Этапы — только пока идёт работа или пока она не удалась.
            У готового ответа от них остаётся серая строка над ним: этапы
            занимают больше места, чем сам ответ, и подпись оказывалась ниже
            подписанного. Неудача — исключение: там объяснение и есть весь
            результат, показывать его строкой нечем. */}
        {/* Как только исход лёг в ленту, карточку сменяет плашка оттуда — с той
            же кнопкой повтора (SMY-352): два объяснения одного сбоя подряд
            читались бы двумя сбоями. */}
        {mine && (!finished || failed) && !inFeed.has(run.id) ? (
          <RunCard
            run={run}
            free={runFree}
            retrying={busy}
            {...(again != null && unsure == null ? { onRetry: () => void retry(again) } : {})}
          />
        ) : null}

        {/* Живой ответ — только пока лента его не перечитала.

            Отказ по содержанию сюда не попадает, и это не потеря: модель
            успевает вытолкнуть в стрим свой отказ («Я не могу обсуждать эту
            тему»), а карточка выше говорит то же самое словами продукта. Показ
            обоих давал два ответа на один вопрос, причём второй — с серой
            строкой про списание, которого не было. */}
        {mine && !blocked && !answered.has(run.id) && run.artifact == null && run.text !== '' ? (
          <>
            {/* «Выполнилось за N с» — только над готовым ответом: над начатым и
                отменённым она читалась бы как выполненный. */}
            {finished && !failed ? (
              <RunLine
                {...(liveDuration != null ? { durationMs: liveDuration } : {})}
                charged={run.charged}
                unit={liveUnit}
                {...(run.sources.length > 0 && liveSourcesAt != null ? { sourcesAt: liveSourcesAt } : {})}
              />
            ) : null}
            <Bubble from="assistant">
              <Answer>{run.text}</Answer>
              {run.sources.length > 0 ? (
                <div className="bubble-sources">
                  <Sources sources={run.sources} />
                </div>
              ) : null}
            </Bubble>
          </>
        ) : null}

        {liveArtifact != null && mine && !answered.has(run.id) ? (
          <>
            <RunLine
              {...(liveDuration != null ? { durationMs: liveDuration } : {})}
              charged={run.charged}
              unit={liveUnit}
              {...(run.sources.length > 0 && liveSourcesAt != null ? { sourcesAt: liveSourcesAt } : {})}
            />
            <ResultCard
              artifact={liveArtifact}
              charged={run.charged}
              unit={liveUnit}
              runId={run.id}
            />
          </>
        ) : null}

        {/* Предложение дорогого сценария — плашкой после ответа, а не внутри
            процесса: оно адресовано человеку, дочитавшему до конца. */}
        {mine && run.offer != null && !inFeed.has(run.id) ? <Notice>{run.offer.text}</Notice> : null}

        {/* Граница ответственности — под ответом. Она его не заменяет и не
            задерживает: обещание продукта — назвать границу, а не спрятать
            результат за ней (docs/disclaimers.md). */}
        {/* По `answered`, как и сам живой ответ: у отменённого и сорвавшегося
            запуска начатый ответ остаётся на экране, а сервер эту плашку в ленту
            не пишет — ответ стоял бы без границы (ворота ревью 05.10.2026). */}
        {mine && !answered.has(run.id)
          ? (run.notices ?? [])
              .filter((n) => n.placement === 'after')
              .map((n) => <Notice key={n.text}>{n.text}</Notice>)
          : null}

        {/* Повтор под последней плашкой сбоя (SMY-352) — ниже живого ответа:
            начатый ответ сорвавшегося запуска остаётся на месте (`answered`), и
            кнопка встаёт после него, а не между плашкой и текстом. Не
            предлагается, пока в чате идёт работа; пока после чтения ленты
            что-то отправлено — до первого события потока запуска на экране ещё
            нет, и второе нажатие завело бы второй запуск; и пока исход
            оборванного старта неизвестен (`unsure`). */}
        {feedRetry != null && !running && sent.length === 0 && unsure == null ? (
          feedRetry === 'file' ? (
            <p className="feed-retry muted">Чтобы запустить ещё раз, прикрепи файл заново и отправь задачу.</p>
          ) : (
            <div className="feed-retry">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => void retry(feedRetry.input)}
              >
                {againLabel(feedRetry.scenario)}
              </button>
              <span className="run__hint">{retryCost(freeLeft > 0, feedRetry.unit)}</span>
            </div>
          )
        ) : null}

        {errorLine != null ? (
          <p className="error-line" role="alert">
            {errorLine}
          </p>
        ) : null}
      </div>

      <div className={`chat__foot${home ? ' chat__foot--home' : ''}`}>
        {file != null ? (
          <div className="attached">
            <Icon name="docReview" size={18} />
            <span className="attached__name">{file.name}</span>
            <button
              type="button"
              className="attached__drop"
              aria-label={`Убрать файл ${file.name}`}
              onClick={() => {
                setFile(null)
                dropCards(shown.current ?? null)
              }}
            >
              <Icon name="close" size={16} strokeWidth={2} />
            </button>
          </div>
        ) : null}

        {/* Предупреждение стоит рядом со скрепкой, а не в справке: читать его
            человек будет ровно в тот момент, когда выбирает документ. */}
        {needsFile && file == null ? <p className="attached__warning">{UPLOAD_WARNING}</p> : null}
        {/* Одна строка, а не две. Сверх предела главнее строка о длине: строка
            о картинке обещала бы, что запрос уйдёт. До предела у картинки
            главнее строка о картинке: запрос уходит, а честное в нём — что
            рисунок учтёт около тысячи знаков (ворота SMY-619, ux-states). */}
        {imageNote != null && !tooLong ? (
          <p className="attached__warning">{imageNote}</p>
        ) : lengthNote != null ? (
          <p className="attached__warning">{lengthNote}</p>
        ) : null}
        {/* Объявляется переход, а не каждый знак: видимая строка меняется на
            каждом нажатии и перебивала бы эхо ввода. Область стоит всегда —
            объявление появившегося узла ненадёжно. */}
        <span className="visually-hidden" role="status">
          {tooLong ? sendClosed : ''}
        </span>

        {/* Бесплатные уточнения кончились — говорим об этом НАД полем и ровно
            тогда, когда человек начал писать следующий вопрос. Постоянная
            плашка на этом месте превратилась бы в фон, который не читают, а
            строка под полем отвечает на другой вопрос: сколько стоит вообще,
            а не «почему прошлые три были бесплатны, а этот нет». */}
        {hadPaid && freeLeft === 0 && text.trim() !== '' ? (
          <p className="chat__toast" role="status">
            {FREE_FOLLOWUPS_SPENT}
          </p>
        ) : null}

        <Composer
          value={text}
          onChange={setText}
          focusKey={focusField}
          label="Опиши задачу"
          placeholder={
            off
              ? 'Эта задача сейчас не запускается'
              : running
                ? 'Работа идёт — можно писать дальше'
                : chosen != null
                  ? 'Опиши задачу'
                  : 'Напиши, что нужно'
          }
          // Пока исход оборванного старта неизвестен, поле закрыто: попытку
          // дошлёт тем же ключом повтор (SMY-449), а отправка из поля — новая
          // попытка с новым ключом и, дойди первая, второй запуск. Строка над
          // полем (`START_UNSURE`) говорит, чего ждать.
          disabled={off || unsure != null}
          busy={busy || uploading}
          sendBlocked={tooLong}
          {...(off || unsure != null ? {} : { onAttach: (f: File) => void attach(f) })}
          onSend={() => void send()}
        />

        {/* Что сейчас происходит с отправленным (SMY-354). Пока вопрос
            разбирается — до секунд, сервер спрашивает классификатор, — прежде
            единственным признаком была бледная кнопка, как при пустом поле, и
            нажатие выглядело несработавшим. Строка стоит на месте цены: о цене
            уже решено, а о происходящем — нет. Живая область есть всегда и в
            покое пуста: так её слышит программа чтения с экрана, а цена,
            которая меняется каждую минуту окна правки, не зачитывается. Обе
            строки — в одной обёртке, чтобы пустая не брала зазор подвала. */}
        <div>
          <p className="chat__hint" role="status">
            {uploading ? 'Принимаю файл' : busy ? 'Читаю задачу' : null}
          </p>

          {/* Что стоит следующий вопрос — одной строкой и в порядке важности.
              Открытое окно бесплатной правки сильнее уточнений: оно короче по
              времени и относится к тому, что человек видит прямо сейчас. */}
          {/* При карточках набранное — ответ на них, а не правка: строка правки обещала
              бы то, чего отправка не сделает (ворота ревью 2, ux-states-checker). */}
          {busy || uploading ? null : (
            <p className="chat__hint">
              {fieldCost === 'free_edit' && editHint != null
                ? editHint
                : fieldCost === 'free_followup'
                  ? `Ещё ${freeLeft} ${pluralize(freeLeft, CLARIFICATION)} в этом чате — без списания лимита`
                  : `Спишется ${formatUnit(1, costUnit)} с лимита — после готового результата`}
            </p>
          )}
        </div>

        {/* Карточки задач — ПОД полем, последними в подвале. Ниже строки про
            списание намеренно: она про поле, а карточки — про другой способ
            начать, и читать их до цены не нужно. */}
        {home ? <Tasks onPick={(slug) => navigate(`/task/${slug}`)} /> : null}
      </div>

      {confirming != null ? (
        <ConfirmSheet
          intent={confirming.intent}
          onCancel={() => setConfirming(null)}
          onRun={async (scenario) => {
            // Окно закрывается ПОСЛЕ старта, а не до (SMY-352). Закрытое до
            // него, оно уносило с собой и обработку отказа: сбой старта —
            // нет связи, отказ сервера — писался в размонтированное окно, и
            // человек, подтвердивший задачу, не видел ни работы, ни причины.
            // Брошенное здесь окно ловит само и показывает словами.
            const at = path.current
            try {
              await go(confirming.request, scenario, confirming.intent.intentId, confirming.field, at, confirming.cards)
            } catch (e) {
              // Кроме обрыва старта: кнопка окна снова была бы активна, а
              // запуск мог уже идти. Окно закрывается, что случилось — говорит
              // строка над полем, которую рисует сама попытка (`unsure`).
              if (!(e instanceof StartUnsure)) throw e
            }
            setConfirming(null)
          }}
        />
      ) : null}

      {/* Просьбы продукта — имя и пароль. Живут на главном экране, а не в
          оболочке: оболочка есть и у разделов аккаунта, а перебивать человека
          посреди разбирательства со списаниями незачем.

          И никогда одновременно с подтверждением списания: то окно человек
          открыл сам и ждёт ответа именно от него. */}
      {confirming == null ? <Onboarding /> : null}

    </Shell>
  )
}

/**
 * Чата нет или он не открылся — объяснение и единственный путь дальше. SMY-356
 *
 * Свой, а не `Nothing` экрана результата: тот сам разворачивает оболочку и
 * ведёт «В чат», а здесь мы уже в чате, у состояния есть заголовок, и путь у
 * двух состояний разный — новый запрос или повтор чтения.
 */
function ChatGone({
  title,
  text,
  action,
  onAction,
}: {
  title: string
  text: string | null
  action: string
  onAction: () => void
}) {
  return (
    <div className="chat__gone" role="status">
      <h2 className="chat__gone-title">{title}</h2>
      {text != null ? <p className="muted">{text}</p> : null}
      <button type="button" className="btn btn--primary" onClick={onAction}>
        {action}
      </button>
    </div>
  )
}

/**
 * Чем спишется следующий вопрос.
 *
 * Из справочника, а не из таблицы в вёрстке: соответствие «сценарий →
 * единица» живёт в конфигурации сервера и меняется из админки без релиза.
 * Ничего не выбрано — считаем обычным вопросом: им и списывается свободный
 * ввод, пока не окажется чем-то другим.
 */
function nextUnit(
  reference: { scenarios: readonly { id: ScenarioId; unit: UnitId }[] } | null,
  intent: Intent | null,
  chosen: ScenarioId | undefined,
): UnitId {
  const scenario = intent?.scenario ?? chosen ?? 'chat'
  return reference?.scenarios.find((s) => s.id === scenario)?.unit ?? 'questions'
}

/** Одна запись ленты: вопрос, ответ, плашка или карточка результата. */
function FeedRow({
  message,
  artifact,
  meta,
  onRate,
  assumptions,
  onAssumption,
  assumptionsPaid = null,
}: {
  message: FeedMessage
  artifact?: ChatArtifact
  /** Что было с запуском: сколько заняло и списалось ли. */
  meta?: RunMeta
  /** Поставить оценку. SMY-123 */
  onRate: (runId: string, input: RatingInput) => Promise<void>
  /** «Исходил из: … Изменить» — только у ответа, где решил экран (SMY-497). */
  assumptions?: readonly Assumption[]
  onAssumption?: (a: Assumption) => void
  /** Правка спишется этой единицей — блок называет цену (В4-Б). */
  assumptionsPaid?: UnitId | null
}) {
  // Строка про запуск стоит НАД ответом, как подпись у цитаты: она объясняет
  // то, что под ней, а не то, что было до.
  const line =
    meta != null && message.role === 'assistant' ? (
      <RunLine
        {...(meta.durationMs != null ? { durationMs: meta.durationMs } : {})}
        charged={meta.charged}
        unit={meta.unit}
        {...(meta.sourcesAt != null ? { sourcesAt: meta.sourcesAt } : {})}
        {...(meta.pastSearchAt != null ? { pastSearchAt: meta.pastSearchAt } : {})}
      />
    ) : null

  // Блок допущений — между ответом и рядом действий: он про ответ, а ряд — про
  // запуск (SMY-497).
  const block =
    assumptions != null && assumptions.length > 0 && onAssumption != null ? (
      <Assumptions list={assumptions} onPick={onAssumption} paid={assumptionsPaid} />
    ) : null

  /**
   * Ряд действий — ПОД ответом и под источниками, последней строкой.
   *
   * Стоит у КАЖДОГО ответа ассистента, а не по расписанию. Правка от
   * 07.09.2026: показывать оценку только у каждого пятого запуска значило
   * лишить человека кнопки ровно тогда, когда ему есть что сказать.
   * Расписание осталось у подписи-вопроса, а не у самих кнопок.
   *
   * Появляется только когда запуск известен: у плашек и у сообщений человека
   * ни копировать, ни оценивать нечего.
   */
  const actions =
    meta != null && message.role === 'assistant' ? (
      <AnswerActions
        // Ответ уходит в кнопку как есть, разметкой: чистый текст из него
        // кнопка делает сама и только на нажатии.
        {...(message.kind === 'text' ? { text: message.body } : {})}
        rating={meta.rating}
        onRate={(input) => onRate(meta.runId, input)}
      />
    ) : null

  /**
   * Источники — ВНУТРИ пузыря, последней строкой. Правка от 07.09.2026.
   *
   * Снаружи они были третьим блоком в столбце «строка запуска — пузырь —
   * источники — кнопки», и экран из четырёх ярусов вокруг одного ответа
   * читался тяжелее самого ответа. Внутри пузыря вопрос «откуда это» стоит
   * там же, где то, о чём он спрашивает.
   *
   * У карточки результата свои, внутри неё, — там они часть самой карточки.
   */
  const sources =
    meta != null && meta.sources.length > 0 && message.kind !== 'artifact' ? (
      <div className="bubble-sources">
        <Sources sources={meta.sources} />
      </div>
    ) : null

  if (message.kind === 'artifact') {
    if (artifact == null) {
      // Сообщение есть, а карточки нет: артефакт удалён вместе с файлом или
      // не сохранился. Молчать нельзя — за результат списана единица.
      return (
        <>
          {line}
          <Bubble from="assistant">{message.body}</Bubble>
          {actions}
        </>
      )
    }
    return (
      <>
        {line}
        <ResultCard
          artifact={artifact}
          charged={artifact.charged}
          unit={artifact.unit}
          runId={artifact.runId}
        />
        {block}
        {actions}
      </>
    )
  }
  if (message.kind === 'notice') return <Notice>{message.body}</Notice>
  // Разметка — только у ассистента: человек пишет обычным текстом, и
  // «звёздочка» в его вопросе должна остаться звёздочкой.
  return (
    <>
      {line}
      <Bubble from={message.role}>
        {/* Приложенный файл — в самом сообщении человека: он его и прислал,
            и в ответе это больше не повторяется. Имя, а не ссылка: файлы
            живут со своим сроком хранения и однажды исчезают, а вопрос «что
            я тогда прислал» остаётся. */}
        {message.attachment != null ? (
          <span className="bubble__file">
            <Icon name="docReview" size={16} strokeWidth={2} />
            {message.attachment}
          </span>
        ) : null}
        {message.role === 'assistant' ? <Answer>{message.body}</Answer> : message.body}
        {sources}
      </Bubble>
      {block}
      {actions}
    </>
  )
}
